import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { FictionalVehicle, fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface Props {
  vehicle: FictionalVehicle;
  pct: number;
  onPctChange: (pct: number) => void;
  downPayment: number;
  loanAmount: number;
  liquidBefore: number;
  emergencyFund: number;
  onContinue: () => void;
  onBack: () => void;
}

const PRESETS = [0, 0.05, 0.1, 0.2];

export default function DownPaymentScreen({ vehicle, pct, onPctChange, downPayment, loanAmount, liquidBefore, emergencyFund, onContinue, onBack }: Props) {
  const cashAfter = liquidBefore - downPayment;
  const efHealthy = cashAfter > emergencyFund;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Down Payment</h2>
        <p className="text-sm text-muted-foreground">Choose how much to pay upfront for your {vehicle.vehicle_name}.</p>
      </div>

      <div className="glass-card glow-border p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Vehicle Price</span>
          <span className="font-heading font-bold text-foreground">{fmt(vehicle.purchase_price)}</span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">Down Payment: {Math.round(pct * 100)}%</span>
            <span className="text-lg font-heading font-bold text-primary">{fmt(downPayment)}</span>
          </div>

          <Slider
            value={[pct * 100]}
            onValueChange={([v]) => onPctChange(v / 100)}
            min={0} max={50} step={1}
            className="w-full"
          />

          <div className="flex gap-2">
            {PRESETS.map(p => (
              <button
                key={p}
                onClick={() => onPctChange(p)}
                className={`flex-1 text-xs py-1.5 rounded-lg border transition-all ${
                  pct === p ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {p === 0 ? '$0' : `${Math.round(p * 100)}%`}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-border">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Loan Amount</span>
            <span className="font-semibold text-foreground">{fmt(loanAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Cash After Down Payment</span>
            <span className={`font-semibold ${cashAfter < 0 ? 'text-destructive' : 'text-foreground'}`}>{fmt(cashAfter)}</span>
          </div>
          <div className={`text-xs text-center py-1.5 rounded-full ${efHealthy ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
            {efHealthy ? '✓ Emergency fund preserved' : '⚠ This depletes beyond your emergency fund'}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1 gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
